"use client";

import { useMemo } from "react";

function checksFor(pw: string) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[^\w\s]/.test(pw),
  };
}

export function firstPolicyError(pw: string): string | null {
  const c = checksFor(pw);
  if (!c.length) return "Password must be at least 8 characters.";
  if (!c.upper) return "Password must include at least 1 uppercase letter.";
  if (!c.number) return "Password must include at least 1 number.";
  if (!c.special) return "Password must include at least 1 special character.";
  return null;
}

function Badge({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={[
          "inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none",
          ok
            ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-200"
            : "border-white/15 bg-white/5 text-neutral-400",
        ].join(" ")}
      >
        {ok ? "✓" : "•"}
      </span>
      <span className={ok ? "text-emerald-200" : "text-neutral-400"}>{children}</span>
    </div>
  );
}

export default function PasswordRequirements({ password }: { password: string }) {
  const checks = useMemo(() => checksFor(password), [password]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
        Password requirements
      </div>
      <div className="mt-2 space-y-1 text-sm">
        <Badge ok={checks.length}>At least 8 characters</Badge>
        <Badge ok={checks.upper}>At least 1 uppercase letter</Badge>
        <Badge ok={checks.number}>At least 1 number</Badge>
        <Badge ok={checks.special}>At least 1 special character</Badge>
      </div>
    </div>
  );
}
